/* Feb-2026 · AP Approval Queue
 *
 * Pending AP journal entries submitted by ledger_entry/ap_ar clerks
 * that credit an AP-class account (200-240) sit here until a CFO
 * (`ap_approver` or `pro_writer` scope) approves or rejects them.
 * Every action is audited server-side.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, RefreshCw, ShieldCheck, Clock } from 'lucide-react';
import { LedgerAccessLink } from './LedgerAccessLink';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

function fmtUSD(v) {
  if (v == null) return '—';
  const sign = v < 0 ? '−' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ApprovalQueuePanel() {
  const [entries, setEntries] = useState([]);
  const [busy, setBusy]         = useState(false);
  const [actingId, setActingId] = useState(null);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get('/admin/gl/pending');
      setEntries(r.data?.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load approval queue.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const approve = async (id) => {
    setActingId(id);
    try {
      await http.post(`/admin/gl/entries/${id}/approve`);
      toast.success('Entry approved and posted.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed.');
    } finally { setActingId(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason (required, ≥3 characters):');
    if (!reason || reason.trim().length < 3) return;
    setActingId(id);
    try {
      await http.post(`/admin/gl/entries/${id}/reject`, { reason: reason.trim() });
      toast.success('Entry rejected.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rejection failed.');
    } finally { setActingId(null); }
  };

  return (
    <section data-testid="ap-approval-queue" className="space-y-3">
      <div className="rounded-md border border-border bg-card p-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <div className="text-[12.5px] font-semibold">AP Approval Queue</div>
          <span className="text-[10.5px] text-muted-foreground">
            Journal entries that credit AP accounts (200–240) held for CFO review.
          </span>
          <button onClick={reload} disabled={busy}
                  className="ml-auto inline-flex h-7 items-center gap-1 rounded-sm border border-border bg-background px-2 text-[11px] shadow-btn hover:shadow-btn-hover disabled:opacity-60"
                  data-testid="ap-queue-refresh">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
          </button>
          <LedgerAccessLink testId="ap-queue-open-ledger" />
        </div>
      </div>

      {entries.length === 0 && (
        <div className="rounded-md border border-dashed border-border/60 p-6 text-center text-[12px] text-muted-foreground" data-testid="ap-queue-empty">
          <CheckCircle2 className="mx-auto mb-2 h-5 w-5 text-success" />
          No pending AP entries — every disbursement is up to date.
        </div>
      )}

      {entries.map((e) => (
        <div key={e.id} className="rounded-md border border-warning/40 bg-warning/5 p-3" data-testid={`ap-queue-row-${e.id}`}>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-warning" />
            <span className="text-[12px] font-semibold">{e.memo}</span>
            {e.reference && <span className="font-mono-stat text-[10.5px] text-muted-foreground">ref: {e.reference}</span>}
            <span className="ml-auto font-mono-stat text-[13px] font-semibold text-warning">{fmtUSD(e.total_usd)}</span>
          </div>
          <div className="mb-2 text-[10.5px] text-muted-foreground">
            Submitted by <span className="font-mono-stat text-foreground">{e.submitter || 'unknown'}</span>
            {' · '}{new Date(e.at).toLocaleString()}
          </div>
          <div className="space-y-0.5 rounded-sm border border-border bg-background p-2">
            {(e.lines || []).map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]">
                <span className="font-mono-stat text-muted-foreground">{l.account_code}</span>
                {(l.debit_usd  > 0) && <span className="text-primary">Dr {fmtUSD(l.debit_usd)}</span>}
                {(l.credit_usd > 0) && <span className="text-success">Cr {fmtUSD(l.credit_usd)}</span>}
                {l.memo && <span className="text-muted-foreground">· {l.memo}</span>}
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button" onClick={() => reject(e.id)} disabled={actingId === e.id}
              className="inline-flex h-8 items-center gap-1 rounded-sm border border-destructive/60 bg-background px-3 text-[12px] font-semibold text-destructive shadow-btn hover:shadow-btn-hover disabled:opacity-60"
              data-testid={`ap-queue-reject-${e.id}`}
            >
              {actingId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />} Reject
            </button>
            <button
              type="button" onClick={() => approve(e.id)} disabled={actingId === e.id}
              className="inline-flex h-8 items-center gap-1 rounded-sm bg-success px-3 text-[12px] font-semibold text-success-foreground shadow-btn hover:shadow-btn-hover disabled:opacity-60"
              data-testid={`ap-queue-approve-${e.id}`}
            >
              {actingId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Approve & post
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}
